import { useDisclosure } from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { useKey } from "react-use"
import { StringParam, useQueryParam } from "use-query-params"
import { shareSections } from "../tabs/ShareTabs"

const ShareUIContext = React.createContext()

export const useShareUIContext = () => React.useContext(ShareUIContext)

const useOpenWithKeys = ({ onKeyCombo, shouldOpen }) => {
  useKey(
    (event) => {
      if (!shouldOpen) return false

      const isWindowsTrigger =
        event.altKey &&
        event.shiftKey &&
        !event.metaKey &&
        event.key &&
        event.key.toLowerCase() === "s"

      const isMacTrigger =
        event.metaKey &&
        event.shiftKey &&
        event.key &&
        event.key.toLowerCase() === "s"

      return isWindowsTrigger || isMacTrigger
    },
    () => {
      onKeyCombo()
    }
  )
}

const ShareUIContextProvider = ({
  children,
  context,
  openWithKeys,
  openWithQueryParam = true,
  defaultOpen = false,
  onToggleIsOpen
}) => {
  const { open, onOpen, onClose, onToggle } = useDisclosure({ defaultOpen })

  const [shareParam, setShareParam] = useQueryParam("share", StringParam)

  useEffect(() => {
    if (openWithQueryParam && shareParam && !open) {
      onToggle()
    }
  }, [shareParam, openWithQueryParam])

  useOpenWithKeys({ onKeyCombo: onToggle, shouldOpen: openWithKeys })

  const [pendingInviteCount, setPendingInviteCount] = React.useState(0)
  const [tabIndex, setTabIndex] = React.useState(0)
  const tabKey = React.useMemo(() => shareSections[tabIndex]?.key, [tabIndex])

  // Track which tab should handle any auto-focus behavior within the tab panel.
  // This could be for auto-focusing the SelectPeople select input, for example.
  // For best UX, we only want to auto-focus the tab panel content when the tab
  // was clicked, not when accessed via keyboard navigation (or by any other means).
  const [clickedTabIndex, setClickedTabIndex] = useState()

  // Reset clickedTabIndex when tabIndex changes. This ensures default focus behavior
  // when subsequently using keyboard navigation.
  useEffect(() => {
    if (tabIndex !== clickedTabIndex) {
      setClickedTabIndex()
    }
  }, [tabIndex])

  const canClose = (e) => {
    // Check if we can close the Share card.
    return !(
      document.querySelector(".InviteQRCode") ||
      document.querySelector(".RoleDescriptorsDrawer") ||
      (document.querySelector(".chakra-toast") &&
        // Allow closing Share card when toast is present as long as user is not
        // clicking in toast (like so as to close the toast).
        [...document.querySelectorAll(".chakra-toast")].some((node) =>
          node.contains(e.target)
        ))
    )
  }

  const handleClose = () => {
    // Remove the "?share=true" param if it exists when closing the card
    if (openWithQueryParam && shareParam) {
      setShareParam(undefined)
    }
    onClose()
  }

  useEffect(() => {
    onToggleIsOpen?.(open)
  }, [open])

  const value = {
    // Context props
    context,

    // State
    pendingInviteCount,
    setPendingInviteCount: () => {
      // console.log("TODO: setPendingInviteCount disabled for demo")
    },
    tabKey,
    tabIndex,
    setTabIndex,
    onClose: handleClose,
    canClose,
    isOpen: open,
    onOpen,
    onToggle,
    clickedTabIndex,
    setClickedTabIndex
  }

  return (
    <ShareUIContext.Provider value={value}>
      {typeof children === "function" ? children(value) : children}
    </ShareUIContext.Provider>
  )
}

ShareUIContextProvider.displayName = "ShareUIContextProvider"

export default ShareUIContextProvider
